import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { debug } from "../../../utils/debugLogger";
import { validateOAuthState } from "../../../utils/oauthState";
import { readPersistedWorkspace } from "../../../utils/workspacePersistence";
import { clearOAuthCustomerId, readOAuthCustomerId } from "../../site-connections/siteConnectionSession";
import {
  exchangeShopifyCode,
  type ShopifyConnection,
} from "../../wordpress/platforms/wordpress/services/blogPublishingService";
import { SHOPIFY_OAUTH_PROVIDER, ShopifyBlogSection } from "./ShopifyBlogSection";

const CONNECT_PATH = "/social-posts?tab=connect&section=website";

/**
 * /connect/shopify/callback (SGL-431). Shopify signs every query param it sends
 * back (hmac), so all of them go to the backend unchanged; it verifies the
 * signature, saves the token and answers with the connection. A store with
 * several blogs then picks one here.
 *
 * Under ProtectedRoute but outside MainLayout, like the Search Console callback:
 * the customer comes from the pin set when Connect was clicked.
 */
const ShopifyCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { idToken } = useAuth();
  const [result, setResult] = useState<ShopifyConnection | null>(null);
  const [customerId, setCustomerId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  // The code is single-use: exchange exactly once, also under StrictMode.
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !idToken) return;
    started.current = true;
    const params = new URLSearchParams(location.search);
    const pinned = readOAuthCustomerId(SHOPIFY_OAUTH_PROVIDER) || readPersistedWorkspace().id || "";
    clearOAuthCustomerId(SHOPIFY_OAUTH_PROVIDER);

    if (params.get("error")) {
      debug.error("Shopify OAuth error:", params.get("error"));
      setError("Shopify wasn't connected. You can connect it whenever you're ready.");
      return;
    }
    if (!validateOAuthState(SHOPIFY_OAUTH_PROVIDER, params.get("state"))) {
      setError("This sign-in could not be checked. Connect Shopify again.");
      return;
    }
    if (!params.get("code") || !params.get("shop")) {
      setError("Shopify didn't send the connection back. Connect Shopify again.");
      return;
    }
    if (!pinned) {
      setError("No business profile selected. Choose one and connect again.");
      return;
    }
    setCustomerId(pinned);
    exchangeShopifyCode(pinned, idToken, Object.fromEntries(params.entries()))
      .then(setResult)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to connect Shopify"));
  }, [idToken, location.search]);

  const back = () => navigate(CONNECT_PATH);

  return (
    <div className="flex min-h-screen flex-col p-4">
      <div className="mb-2 flex w-full justify-start pl-4">
        <button type="button" onClick={back} className="flex items-center gap-2 font-medium text-gray-600 hover:text-gray-900">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      </div>
      <div className="flex w-full flex-1 flex-col items-center">
        <div className="w-full max-w-2xl">
          {error ? (
            <div className="rounded-xl bg-white p-6 shadow-xl">
              <h1 className="text-xl font-semibold text-gray-800">Shopify not connected</h1>
              <p role="alert" className="mt-2 text-sm text-red-700">
                {error}
              </p>
              <button
                type="button"
                onClick={back}
                className="mt-4 rounded-lg bg-theme-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90"
              >
                Back to Connections
              </button>
            </div>
          ) : !result || !idToken ? (
            <div className="flex flex-col items-center justify-center py-24" role="status">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-theme-primary" />
              <p className="text-gray-600">Connecting your Shopify store…</p>
            </div>
          ) : (
            <>
              <ShopifyBlogSection customerId={customerId} idToken={idToken} initial={result} onChange={setResult} inDialog />
              {result.status === "CONNECTED" && (
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={back}
                    className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90"
                  >
                    Done
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopifyCallback;
